import { useQuery, useMutation } from '@apollo/client/react/index.js';
import {
  GlassAvatar,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassConfirmDialog,
  GlassInput,
  GlassSelect,
  GlassTabPanel,
  GlassTabs,
  GlassTable,
  GlassTableHead,
  GlassTableBody,
  GlassTableRow,
  GlassTableHeaderCell,
  GlassTableCell,
} from '@merritrac/shared/components';
import { hashPassword } from '@merritrac/shared/utils';
import { ArrowLeft, KeyRound, ShieldOff, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  CREATE_USER,
  DELETE_USER,
  GET_ACCOUNTS,
  GET_SCREENS,
  GET_USER,
  GET_USERS,
  GET_USER_SCREENS,
  UPDATE_USER,
} from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

/* ── Types ── */

type AdminRole = 'admin' | 'manager' | 'technician' | 'viewer';
type PortalRole = 'standard' | 'readonly';
type UserStatus = 'active' | 'inactive';

type PermissionLevel = 'none' | 'read' | 'write';

interface ScreenPermission {
  screen: string;
  level: PermissionLevel;
}

interface AdminUser {
  id: string;
  kind: 'admin';
  name: string;
  email: string;
  role: AdminRole;
  status: UserStatus;
  lastLogin: string;
  account: string;
  permissions: ScreenPermission[];
}

interface PortalUser {
  id: string;
  kind: 'portal';
  name: string;
  email: string;
  username: string;
  role: PortalRole;
  status: UserStatus;
  lastLogin: string;
  account: string;
  group: string;
  permissions: ScreenPermission[];
}

type AppUser = AdminUser | PortalUser;

interface LoginEvent {
  id: string;
  date: string;
  ip: string;
  device: string;
  result: 'success' | 'failed';
}

interface ActionEvent {
  id: string;
  date: string;
  action: string;
  target: string;
}

/* ── Constants ── */

const SCREENS = [
  'Dashboard',
  'Service Orders',
  'Units',
  'Checklists',
  'Users',
  'Time Tracking',
  'Settings',
] as const;

const defaultPermissions: ScreenPermission[] = SCREENS.map((screen) => ({
  screen,
  level: 'write' as PermissionLevel,
}));

const ADMIN_USER_TYPES = new Set(['super_admin', 'admin', 'viewer', 'manager', 'technician']);

function isAdminUserType(userType: string | null | undefined): boolean {
  return ADMIN_USER_TYPES.has(userType ?? '');
}

/* ── GraphQL Response Types ── */

interface UserNode {
  id: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  groupId: number | null;
  userType: string | null;
  active: number;
  lastLogin: string | null;
}

interface GetUserData {
  user: UserNode | null;
}

interface ScreenNode {
  id: number;
  name: string;
  parentId: number | null;
  forbidden: number;
}

interface UserScreenNode {
  screenName: string | null;
  level: string;
}

interface GetScreensData {
  screens: ScreenNode[];
}

interface GetUserScreensData {
  userScreens: UserScreenNode[];
}

function apiNodeToAppUser(node: UserNode): AppUser {
  const name =
    [node.firstName, node.lastName].filter((s): s is string => s !== null).join(' ') ||
    (node.username ?? '—');
  const isAdmin = isAdminUserType(node.userType);

  if (isAdmin) {
    const rawRole = (node.userType ?? 'viewer').toLowerCase();
    const role: AdminRole = ['admin', 'manager', 'technician', 'viewer'].includes(rawRole)
      ? (rawRole as AdminRole)
      : 'viewer';
    return {
      id: node.id,
      kind: 'admin',
      name,
      email: node.email ?? '',
      role,
      status: node.active ? 'active' : 'inactive',
      lastLogin: node.lastLogin ?? '—',
      account: '—',
      permissions: defaultPermissions,
    };
  }

  return {
    id: node.id,
    kind: 'portal',
    name,
    email: node.email ?? '',
    username: node.username ?? '',
    role: 'standard',
    status: node.active ? 'active' : 'inactive',
    lastLogin: node.lastLogin ?? '—',
    account: '—',
    group: String(node.groupId ?? '—'),
    permissions: defaultPermissions,
  };
}

const FALLBACK_USER: AdminUser = {
  id: 'new',
  kind: 'admin',
  name: '',
  email: '',
  role: 'viewer',
  status: 'active',
  lastLogin: '—',
  account: '',
  permissions: SCREENS.map((screen) => ({ screen, level: 'read' as PermissionLevel })),
};

/* ── Option lists ── */

const adminRoleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'technician', label: 'Technician' },
  { value: 'viewer', label: 'Viewer' },
] as const;

const portalRoleOptions = [
  { value: 'standard', label: 'Standard' },
  { value: 'readonly', label: 'Read Only' },
] as const;

const permissionLevelOptions = [
  { value: 'none', label: 'No access' },
  { value: 'read', label: 'Read' },
  { value: 'write', label: 'Read & Write' },
] as const;

const detailTabs = [
  { id: 'details', label: 'Details' },
  { id: 'permissions', label: 'Permissions' },
  { id: 'activity', label: 'Activity' },
] as const;

type DetailTabId = (typeof detailTabs)[number]['id'];

/* ── Helpers ── */

function statusVariant(status: UserStatus): 'success' | 'default' {
  return status === 'active' ? 'success' : 'default';
}

/* ── Sub-components ── */

interface ProfileSectionProps {
  user: AppUser;
}

function ProfileSection({ user }: ProfileSectionProps): ReactElement {
  return (
    <GlassCard>
      <GlassCardContent className="flex flex-col sm:flex-row items-start sm:items-center gap-5 py-5">
        <GlassAvatar
          name={user.name !== '' ? user.name : 'New User'}
          size="lg"
          className="w-14 h-14 text-base shrink-0"
        />
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-text truncate">
            {user.name !== '' ? user.name : 'New User'}
          </h2>
          <p className="text-sm text-text-secondary truncate">
            {user.email !== '' ? user.email : '—'}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <GlassBadge variant={user.kind === 'admin' ? 'info' : 'default'}>
              {user.kind === 'admin' ? 'Admin User' : 'Portal User'}
            </GlassBadge>
            <GlassBadge variant={statusVariant(user.status)}>
              {user.status === 'active' ? 'Active' : 'Inactive'}
            </GlassBadge>
          </div>
        </div>
        <div className="text-sm text-text-secondary shrink-0">
          <p className="text-xs font-medium text-text-secondary mb-0.5">Last login</p>
          <p className="text-text">{user.lastLogin}</p>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}

/* ── Details tab form ── */

interface DetailsFormState {
  name: string;
  email: string;
  role: string;
  account: string;
  username: string;
  password: string;
  accountId: string;
}

interface AccountOption {
  value: string;
  label: string;
}

interface DetailsTabProps {
  user: AppUser;
  form: DetailsFormState;
  isNew: boolean;
  accountOptions: AccountOption[];
  onChange: (field: keyof DetailsFormState, value: string) => void;
}

function DetailsTab({
  user,
  form,
  isNew,
  accountOptions,
  onChange,
}: DetailsTabProps): ReactElement {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      {isNew && (
        <>
          <GlassInput
            label="Username"
            value={form.username}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              onChange('username', e.target.value);
            }}
            placeholder="Username for login"
            required
          />
          <GlassInput
            label="Password"
            type="password"
            value={form.password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              onChange('password', e.target.value);
            }}
            placeholder="Initial password"
            required
          />
          <GlassSelect
            label="Account"
            value={form.accountId}
            options={[{ value: '', label: 'Select an account…' }, ...accountOptions]}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
              onChange('accountId', e.target.value);
            }}
          />
        </>
      )}
      <GlassInput
        label="Full Name"
        value={form.name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
          onChange('name', e.target.value);
        }}
        placeholder="Full name"
      />
      <GlassInput
        label="Email Address"
        type="email"
        value={form.email}
        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
          onChange('email', e.target.value);
        }}
        placeholder="user@example.com"
      />
      <GlassSelect
        label="Role"
        value={form.role}
        options={user.kind === 'admin' ? adminRoleOptions : portalRoleOptions}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
          onChange('role', e.target.value);
        }}
      />
      {!isNew && (
        <GlassInput
          label="Account / Organisation"
          value={form.account}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            onChange('account', e.target.value);
          }}
          placeholder="e.g. Acme Corp"
        />
      )}
      {user.kind === 'portal' && (
        <GlassInput
          label="Group"
          value={user.group}
          readOnly
          className="bg-bg-subtle cursor-not-allowed"
        />
      )}
    </div>
  );
}

/* ── Permissions tab ── */

interface PermissionsTabProps {
  permissions: ScreenPermission[];
  onLevelChange: (screen: string, level: PermissionLevel) => void;
}

function PermissionsTab({ permissions, onLevelChange }: PermissionsTabProps): ReactElement {
  return (
    <div>
      <p className="text-sm text-text-secondary mb-4">
        Control which screens this user can access and at what level.
      </p>
      <div className="border border-border rounded-lg overflow-hidden">
        <GlassTable>
          <GlassTableHead>
            <GlassTableRow>
              <GlassTableHeaderCell>Screen</GlassTableHeaderCell>
              <GlassTableHeaderCell>Access Level</GlassTableHeaderCell>
            </GlassTableRow>
          </GlassTableHead>
          <GlassTableBody>
            {permissions.map((perm) => (
              <GlassTableRow key={perm.screen}>
                <GlassTableCell className="font-medium text-text">{perm.screen}</GlassTableCell>
                <GlassTableCell>
                  <GlassSelect
                    options={permissionLevelOptions}
                    value={perm.level}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                      onLevelChange(perm.screen, e.target.value as PermissionLevel);
                    }}
                    className="w-40"
                  />
                </GlassTableCell>
              </GlassTableRow>
            ))}
          </GlassTableBody>
        </GlassTable>
      </div>
    </div>
  );
}

/* ── Activity tab ── */

// Login history and action log remain static placeholders; the API does not expose these.
const mockLoginHistory: LoginEvent[] = [];
const mockActionLog: ActionEvent[] = [];

function ActivityTab(): ReactElement {
  return (
    <div className="space-y-6">
      {/* Login history */}
      <div>
        <h3 className="text-sm font-semibold text-text mb-3">Login History</h3>
        <div className="border border-border rounded-lg overflow-hidden">
          <GlassTable>
            <GlassTableHead>
              <GlassTableRow>
                <GlassTableHeaderCell>Date / Time</GlassTableHeaderCell>
                <GlassTableHeaderCell>IP Address</GlassTableHeaderCell>
                <GlassTableHeaderCell>Device</GlassTableHeaderCell>
                <GlassTableHeaderCell>Result</GlassTableHeaderCell>
              </GlassTableRow>
            </GlassTableHead>
            <GlassTableBody>
              {mockLoginHistory.length === 0 ? (
                <GlassTableRow>
                  <GlassTableCell colSpan={4} className="text-center text-sm text-text-tertiary">
                    No login history available.
                  </GlassTableCell>
                </GlassTableRow>
              ) : (
                mockLoginHistory.map((event) => (
                  <GlassTableRow key={event.id}>
                    <GlassTableCell className="text-text-secondary font-mono text-xs">
                      {event.date}
                    </GlassTableCell>
                    <GlassTableCell className="text-text-secondary font-mono text-xs">
                      {event.ip}
                    </GlassTableCell>
                    <GlassTableCell className="text-text-secondary">{event.device}</GlassTableCell>
                    <GlassTableCell>
                      <GlassBadge variant={event.result === 'success' ? 'success' : 'danger'}>
                        {event.result === 'success' ? 'Success' : 'Failed'}
                      </GlassBadge>
                    </GlassTableCell>
                  </GlassTableRow>
                ))
              )}
            </GlassTableBody>
          </GlassTable>
        </div>
      </div>

      {/* Action log */}
      <div>
        <h3 className="text-sm font-semibold text-text mb-3">Recent Actions</h3>
        <div className="border border-border rounded-lg overflow-hidden">
          <GlassTable>
            <GlassTableHead>
              <GlassTableRow>
                <GlassTableHeaderCell>Date / Time</GlassTableHeaderCell>
                <GlassTableHeaderCell>Action</GlassTableHeaderCell>
                <GlassTableHeaderCell>Target</GlassTableHeaderCell>
              </GlassTableRow>
            </GlassTableHead>
            <GlassTableBody>
              {mockActionLog.length === 0 ? (
                <GlassTableRow>
                  <GlassTableCell colSpan={3} className="text-center text-sm text-text-tertiary">
                    No recent actions available.
                  </GlassTableCell>
                </GlassTableRow>
              ) : (
                mockActionLog.map((event) => (
                  <GlassTableRow key={event.id}>
                    <GlassTableCell className="text-text-secondary font-mono text-xs">
                      {event.date}
                    </GlassTableCell>
                    <GlassTableCell className="text-text">{event.action}</GlassTableCell>
                    <GlassTableCell className="text-text-secondary font-mono text-xs">
                      {event.target}
                    </GlassTableCell>
                  </GlassTableRow>
                ))
              )}
            </GlassTableBody>
          </GlassTable>
        </div>
      </div>
    </div>
  );
}

/* ── Page ── */

export function UserDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const isNew = id === 'new' || id === undefined;
  const numericId = isNew ? 0 : parseInt(id, 10);

  const {
    data: rawData,
    loading,
    error,
  } = useQuery(GET_USER, {
    variables: { id: numericId },
    skip: isNew,
  });
  const data = rawData as GetUserData | undefined;

  const [createUser] = useMutation(CREATE_USER, {
    refetchQueries: [{ query: GET_USERS, variables: { first: 50 } }],
  });
  const [updateUser, { loading: saving }] = useMutation(UPDATE_USER);
  const [deleteUser, { loading: deletingUser }] = useMutation(DELETE_USER, {
    refetchQueries: [{ query: GET_USERS, variables: { first: 50 } }],
  });
  const [showDelete, setShowDelete] = useState(false);

  // Fetch accounts for the account selector (new user only)
  const { data: rawAccountsData } = useQuery(GET_ACCOUNTS, {
    variables: { first: 100 },
    skip: !isNew,
  });
  const accountOptions: AccountOption[] =
    (
      rawAccountsData as
        | { accounts?: { edges: { node: { id: number; name: string } }[] } }
        | undefined
    )?.accounts?.edges.map((e) => ({
      value: String(e.node.id),
      label: e.node.name,
    })) ?? [];

  // Fetch available screens from the backend
  const { data: rawScreensData } = useQuery(GET_SCREENS);
  const screensData = rawScreensData as GetScreensData | undefined;
  const availableScreens: string[] = screensData?.screens
    .filter((s) => s.forbidden === 0)
    .map((s) => s.name) ?? [...SCREENS];

  // Fetch existing user screen permissions
  const { data: rawUserScreensData } = useQuery(GET_USER_SCREENS, {
    variables: { userId: numericId },
    skip: isNew,
  });
  const userScreensData = rawUserScreensData as GetUserScreensData | undefined;

  const apiUser: AppUser | undefined =
    data?.user === null || data?.user === undefined ? undefined : apiNodeToAppUser(data.user);

  const resolvedUser: AppUser = apiUser ?? FALLBACK_USER;

  const [activeTab, setActiveTab] = useState<DetailTabId>('details');
  const [isDirty, setIsDirty] = useState(false);

  const [form, setForm] = useState<DetailsFormState>({
    name: resolvedUser.name,
    email: resolvedUser.email,
    role: resolvedUser.role,
    account: resolvedUser.account,
    username: '',
    password: '',
    accountId: '',
  });

  const [permissions, setPermissions] = useState<ScreenPermission[]>(
    resolvedUser.permissions.map((p) => ({ ...p })),
  );

  // Sync form when data loads
  useEffect(() => {
    if (apiUser !== undefined) {
      setForm((prev) => ({
        ...prev,
        name: apiUser.name,
        email: apiUser.email,
        role: apiUser.role,
        account: apiUser.account,
      }));
      setIsDirty(false);
    }
  }, [data]);

  // Sync permissions from real user screen data when loaded
  useEffect(() => {
    if (userScreensData !== undefined && availableScreens.length > 0) {
      const userPermMap = new Map<string, PermissionLevel>();
      for (const us of userScreensData.userScreens) {
        if (us.screenName !== null) {
          userPermMap.set(us.screenName, us.level as PermissionLevel);
        }
      }
      setPermissions(
        availableScreens.map((screen) => ({
          screen,
          level: userPermMap.get(screen) ?? 'none',
        })),
      );
    }
  }, [rawUserScreensData, rawScreensData]);

  function handleFieldChange(field: keyof DetailsFormState, value: string): void {
    setForm((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  }

  function handlePermissionChange(screen: string, level: PermissionLevel): void {
    setPermissions((prev) =>
      prev.map((p) => (p.screen === screen ? { screen: p.screen, level } : p)),
    );
    setIsDirty(true);
  }

  function handleSave(e: React.SyntheticEvent): void {
    e.preventDefault();
    const nameParts = form.name.trim().split(/\s+/);
    const firstName = nameParts[0] ?? '';
    const lastName = nameParts.slice(1).join(' ');

    if (isNew) {
      if (form.username.trim() === '' || form.password.trim() === '' || form.accountId === '') {
        return;
      }
      const { hashedPasswordBase64, saltBase64 } = hashPassword(form.password);
      void createUser({
        variables: {
          username: form.username,
          passwordHash: hashedPasswordBase64,
          passwordSalt: saltBase64,
          accountId: parseInt(form.accountId, 10),
          firstName: firstName !== '' ? firstName : undefined,
          lastName: lastName !== '' ? lastName : undefined,
          email: form.email !== '' ? form.email : undefined,
          userType: form.role !== '' ? form.role : 'Standard',
        },
      }).then((res) => {
        const data = res.data as { createUser?: { id: number } } | undefined;
        const newId = data?.createUser?.id;
        if (newId !== undefined) {
          void navigate(`/users/${String(newId)}`);
        }
      });
      return;
    }

    void updateUser({
      variables: {
        id: numericId,
        firstName,
        lastName,
        email: form.email,
        screens: permissions.map((p) => `${p.screen}:${p.level}`),
      },
    }).then(() => {
      setIsDirty(false);
    });
  }

  function handleCancel(): void {
    const base = apiUser ?? FALLBACK_USER;
    setForm((prev) => ({
      ...prev,
      name: base.name,
      email: base.email,
      role: base.role,
      account: base.account,
    }));
    setPermissions(base.permissions.map((p) => ({ ...p })));
    setIsDirty(false);
  }

  function handleBack(): void {
    void navigate('/users');
  }

  function handleResetPassword(): void {
    // In production: trigger reset password flow
    /* noop */
  }

  function handleDeactivate(): void {
    // In production: trigger deactivation with confirmation dialog
    /* noop */
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-text-secondary">
        Loading…
      </div>
    );
  }

  if (error !== undefined) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <p className="text-sm text-danger">Failed to load user: {error.message}</p>
        <GlassButton variant="secondary" size="md" onClick={handleBack}>
          <ArrowLeft size={14} />
          Back to Users
        </GlassButton>
      </div>
    );
  }

  const displayUser = apiUser ?? resolvedUser;

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <GlassButton variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft size={14} />
            Back to Users
          </GlassButton>
          <span className="text-border text-text-secondary" aria-hidden>
            |
          </span>
          <h1 className="text-xl font-semibold text-text">
            {isNew ? 'New User' : displayUser.name !== '' ? displayUser.name : 'User Detail'}
          </h1>
        </div>

        {/* Danger-zone actions — hidden for new user */}
        {!isNew && (
          <div className="flex items-center gap-2">
            <GlassButton variant="secondary" size="sm" onClick={handleResetPassword}>
              <KeyRound size={13} />
              Reset Password
            </GlassButton>
            <GlassButton variant="danger" size="sm" onClick={handleDeactivate}>
              <ShieldOff size={13} />
              Deactivate User
            </GlassButton>
          </div>
        )}
      </div>

      {/* Profile banner */}
      {!isNew && <ProfileSection user={displayUser} />}

      {/* Detail card */}
      <GlassCard>
        <GlassCardHeader>
          <GlassTabs
            tabs={detailTabs}
            activeTab={activeTab}
            onTabChange={(tabId): void => {
              setActiveTab(tabId as DetailTabId);
            }}
          />
        </GlassCardHeader>

        <GlassTabPanel>
          <GlassCardContent>
            {activeTab === 'details' && (
              <DetailsTab
                user={displayUser}
                form={form}
                isNew={isNew}
                accountOptions={accountOptions}
                onChange={handleFieldChange}
              />
            )}
            {activeTab === 'permissions' && (
              <PermissionsTab permissions={permissions} onLevelChange={handlePermissionChange} />
            )}
            {activeTab === 'activity' && <ActivityTab />}
          </GlassCardContent>
        </GlassTabPanel>
      </GlassCard>

      {/* Save / Cancel bar — only shown on details and permissions tabs */}
      {(activeTab === 'details' || activeTab === 'permissions') && (
        <div className="flex items-center justify-between py-2">
          {!isNew ? (
            <GlassButton
              variant="ghost"
              size="sm"
              className="text-danger hover:text-danger hover:bg-danger-soft"
              onClick={() => {
                setShowDelete(true);
              }}
            >
              <Trash2 size={14} />
              Delete User
            </GlassButton>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-3">
            <GlassButton
              variant="secondary"
              size="md"
              onClick={handleCancel}
              disabled={!isDirty || saving}
            >
              Cancel
            </GlassButton>
            <GlassButton
              variant="primary"
              size="md"
              onClick={handleSave}
              disabled={!isDirty || saving}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </GlassButton>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <GlassConfirmDialog
        open={showDelete}
        title="Delete User"
        message={`Are you sure you want to delete "${resolvedUser.name}"? This action cannot be undone.`}
        confirmLabel="Delete User"
        variant="danger"
        loading={deletingUser}
        onCancel={() => {
          setShowDelete(false);
        }}
        onConfirm={() => {
          void deleteUser({ variables: { id: numericId } }).then(() => {
            void navigate('/users');
          });
        }}
      />
    </div>
  );
}
